import ActiveController from "rapid/controller";
import {getAttachmentFontFamily} from "../../../utility/font-face.helper";
import {
    clampScreenArea,
    getScreenBox,
    moveScreenArea,
    snapToGuides,
    getBoxGuides
} from "../../../constants/individual-text.constant";

const DONE_ICON = require("../../../../images/icons/done-btn-icon.svg");
// Snap distance in px of the preview
const SNAP_DISTANCE = 6;
// Explanation line height, in font sizes (the scss line-height)
const EXPLANATION_LINE_HEIGHT = 1.1;

export class individualTextScreenPreviewController extends ActiveController {
    constructor($injector, $scope) {
        "ngInject";
        super($injector);

        this.$scope = $scope;
        this.onFontLoad = () => this.$scope.$applyAsync();
        this.guides = {x: [], y: []};
        this.measureContext = document.createElement("canvas").getContext("2d");
    }

    getScale() {
        return Number(this.index) || 1;
    }

    getScreenStyle() {
        const scale = this.getScale();
        const background = this.getBackground();
        return {
            width: this.screenWidth / scale + "px",
            height: this.screenHeight / scale + "px",
            backgroundImage: background && !this.isVideo(background) ? `url("${background}")` : "none"
        };
    }

    getBackground() {
        const background = this.settings.background;
        return background && background.attachment_file;
    }

    isVideo(file) {
        return typeof file === "string" && /\.(mp4|webm|mov)$/i.test(file);
    }

    getAreaStyle(area) {
        const box = getScreenBox(area);
        return {
            width: box.width + "%",
            height: box.height + "%",
            left: box.left + "%",
            top: box.top + "%"
        };
    }

    getFont(attachment, fontSize, color) {
        const family = getAttachmentFontFamily(attachment, this.onFontLoad);
        return {
            fontFamily: family ? `"${family}", sans-serif` : "sans-serif",
            fontSize: fontSize / this.getScale() + "px",
            color
        };
    }

    getExplanationStyle() {
        const explanation = this.settings.explanation;
        return Object.assign(
            this.getAreaStyle(explanation.area),
            this.getFont(explanation.font, this.getExplanationFontSize(), explanation.color)
        );
    }

    /** Lines of the text wrapped by words at maxWidth, with the measure context font. */
    countLines(text, maxWidth) {
        let lines = 1;
        let line = "";
        text.split(/\s+/).filter(Boolean).forEach((word) => {
            const next = line ? line + " " + word : word;
            if (line && this.measureContext.measureText(next).width > maxWidth) {
                lines++;
                line = word;
            } else {
                line = next;
            }
        });
        return lines;
    }

    /**
     * Like the text on the output picture: the biggest size from fontSize down to minFontSize
     * at which the wrapped text fits the area (screen px). Text that does not fit at minFontSize is cut.
     */
    getExplanationFontSize() {
        const explanation = this.settings.explanation;
        const text = explanation.text || "Explanation";
        const box = getScreenBox(explanation.area);
        const width = box.width * this.screenWidth / 100;
        const height = box.height * this.screenHeight / 100;
        const family = this.getFont(explanation.font, 1).fontFamily;
        const fits = (size) => {
            this.measureContext.font = `${size}px ${family}`;
            return this.countLines(text, width) * size * EXPLANATION_LINE_HEIGHT <= height
                && text.split(/\s+/).every((word) => this.measureContext.measureText(word).width <= width);
        };
        const max = Number(explanation.fontSize) || 1;
        const min = Math.min(Number(explanation.minFontSize) || max, max);
        if (fits(max)) {
            return max;
        }
        // Binary search of the biggest fitting size, 0.5 px precision
        let low = min;
        let high = max;
        while (high - low > 0.5) {
            const middle = (low + high) / 2;
            if (fits(middle)) {
                low = middle;
            } else {
                high = middle;
            }
        }
        return low;
    }

    getInputStyle(field) {
        const style = this.getFont(field.font, field.input.fontSize, field.input.textColor);
        style.backgroundColor = field.label.color;
        return style;
    }

    getDoneIcon() {
        const icon = this.settings.doneButton.customIcon;
        return icon && icon.attachment_file || DONE_ICON;
    }

    /** Areas shown on the screen. */
    getVisibleAreas() {
        const settings = this.settings;
        const areas = [settings.outputPreview, settings.doneButton];
        if (settings.explanation.enabled) {
            areas.push(settings.explanation.area);
        }
        settings.fields.forEach((field) => field.enabled && areas.push(field.area));
        return areas;
    }

    getGuideStyle(axis, value) {
        return axis === "x" ? {left: value + "%"} : {top: value + "%"};
    }

    onDrag(area, phase, dx, dy) {
        if (phase === "start") {
            this.dragStart = getScreenBox(area);
            return;
        }
        if (phase === "end") {
            this.guides = {x: [], y: []};
            return;
        }
        const scale = this.getScale();
        const start = this.dragStart;
        const guides = {x: [0, 50, 100], y: [0, 50, 100]};
        this.getVisibleAreas().filter((other) => other !== area).forEach((other) => {
            const box = getScreenBox(other);
            guides.x.push(...getBoxGuides(box.left, box.width));
            guides.y.push(...getBoxGuides(box.top, box.height));
        });
        const snapX = snapToGuides(start.left + dx * scale / this.screenWidth * 100, start.width,
            guides.x, SNAP_DISTANCE * scale / this.screenWidth * 100);
        const snapY = snapToGuides(start.top + dy * scale / this.screenHeight * 100, start.height,
            guides.y, SNAP_DISTANCE * scale / this.screenHeight * 100);
        moveScreenArea(area, snapX.start, snapY.start);
        clampScreenArea(area);
        this.guides = {
            x: snapX.guide === null ? [] : [snapX.guide],
            y: snapY.guide === null ? [] : [snapY.guide]
        };
    }
}



// WEBPACK FOOTER //
// ./sources/components/common/individual-text-screen-preview/individual-text-screen-preview.controller.js